import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../../utils/api';

const TermsAndConditionDrawer = ({ isOpen, onClose, term, onSaved }) => {
  const [formData, setFormData] = useState({
    id: null,
    section_title: '',
    section_html: '',
    status: true,
  });
  const [error, setError] = useState('');

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: ['', 'center', 'right', 'justify'] }],
      [{ color: [] }],
      ['clean'],
    ],
  };

  useEffect(() => {
    if (term) {
      setFormData({
        id: term.id,
        section_title: term.section_title || '',
        section_html: term.section_html || '<p></p>', // Prevent blank input
        status: term.status === 'active',
      });
    } else {
      // Reset form when no term is provided or drawer closes
      setFormData({
        id: null,
        section_title: '',
        section_html: '<p></p>',
        status: true,
      });
    }
  }, [term, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleHtmlChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      section_html: value || '<p></p>', // Prevent blank HTML
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.section_title.trim()) {
      setError('El título es requerido');
      return;
    }
    if (!formData.section_html || formData.section_html === '<p></p>' || formData.section_html === '<p><br></p>') {
      setError('El contenido es requerido');
      return;
    }
    setError('');
    try {
      await api.put(`/api/terminos/${formData.id}`, {
        section_title: formData.section_title,
        section_html: formData.section_html, // Send raw HTML
        status: formData.status ? 'active' : 'inactive',
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar término');
      console.error('Error updating term:', err);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 max-w-full flex sm:w-full sm:max-w-[700px]">
        <DialogPanel className="w-full bg-white shadow-2xl rounded-l-lg flex flex-col h-screen sm:rounded-r-lg">
          <div className="p-6 overflow-y-auto flex-1 space-y-6">
            <div>
              <DialogTitle className="text-2xl font-bold text-gray-900">Editar Término</DialogTitle>
              <p className="mt-2 text-sm text-gray-600">Actualiza los detalles del término. Los campos con * son requeridos.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">Estado del Término</label>
                    <p className="text-xs text-gray-500 mt-1">Activa o desactiva el término.</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="status"
                      name="status"
                      type="checkbox"
                      checked={formData.status}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, status: !prev.status }))}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        formData.status ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                      role="switch"
                      aria-checked={formData.status}
                      aria-labelledby="status"
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          formData.status ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="section_title" className="block text-sm font-medium text-gray-700">Título *</label>
                <p className="text-xs text-gray-500 mt-1">El título de la sección del término.</p>
                <input
                  id="section_title"
                  type="text"
                  name="section_title"
                  value={formData.section_title}
                  onChange={handleChange}
                  placeholder="Título del término"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="section_html" className="block text-sm font-medium text-gray-700">Contenido *</label>
                <p className="text-xs text-gray-500 mt-1">El contenido HTML de la sección del término.</p>
                <div className="mt-1">
                  <ReactQuill
                    id="section_html"
                    theme="snow"
                    value={formData.section_html}
                    onChange={handleHtmlChange}
                    modules={quillModules}
                    className="border border-gray-200 rounded-md shadow-sm focus-within:ring-blue-500 focus-within:border-blue-500 sm:text-sm"
                    placeholder="Contenido del término"
                  />
                </div>
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
            </form>
          </div>
          <div className="bg-gray-50 border-t px-6 py-4 flex justify-between gap-4 sticky bottom-0">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cerrar
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Guardar Cambios
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default TermsAndConditionDrawer;